import { useCallback, useEffect } from 'react';
import { callPopupStore, selectCalls } from '@/features/calls/popups/callPopupStore';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useStoreValue } from '@/lib/tinyStore';
import { notificationActions } from '../notificationStore';
import { popupActions, popupStore, selectPopupRows, type MessagePopupRow } from '../popupStore';
import { MessagePopupCard } from './MessagePopupCard';

const SWEEP_MS = 1000;

/**
 * Renders the message popup only while rows exist, and runs its 1s expiry
 * sweep only then — an idle tab has no timer at all.
 */
export function MessagePopupStack() {
  const rows = useStoreValue(popupStore, selectPopupRows);
  const ringing = useStoreValue(callPopupStore, selectCalls);
  const navigate = useNavigate();
  const hasRows = rows.length > 0;

  /*
   * A RINGING CALL OWNS THE CORNER.
   *
   * Both stacks are anchored bottom-right in the same fixed column, so a chat
   * message, a reaction, a reminder and a channel post all pile up around the
   * one thing on screen that EXPIRES IF YOU MISS IT. Reported as popups
   * "mixing up" during an incoming call, and it is exactly that: four
   * notifications competing with the Accept button.
   *
   * Teams' rule is the one worth copying — a call outranks everything else,
   * and messages simply wait. Nothing is lost: these rows stay in the popup
   * store, keep their unread state, and reappear the moment the ring is
   * answered, declined or times out. This suppresses the DISPLAY, not the
   * notification.
   */
  const callInProgress = ringing.length > 0;

  useEffect(() => {
    if (!hasRows) return undefined;
    const timer = window.setInterval(() => popupActions.sweep(), SWEEP_MS);
    return () => window.clearInterval(timer);
  }, [hasRows]);

  const onOpen = useCallback(
    (row: MessagePopupRow) => {
      notificationActions.markRead(row.id);
      popupActions.dismiss(row.id);
      navigate(row.deepLink ?? ROUTES.chat);
    },
    [navigate],
  );

  const onViewAll = useCallback(() => {
    popupActions.dismissAll();
    navigate(ROUTES.chat);
  }, [navigate]);

  /*
   * WHO DRAWS THE BANNER IS DECIDED ONCE, IN `ingest` - NOT AGAIN HERE.
   *
   * In the desktop client the shell raises a native Windows toast - drawn by
   * the OS, over whatever the user is working in - and `ingest` pushes no row,
   * so there is normally nothing here to render.
   *
   * This card used to refuse to render inside the shell regardless, as "belt
   * and braces". That made the two cases where `ingest` DOES hand the banner
   * to this card invisible: the shell's "Native notifications" switched off,
   * and desktop banners turned off in notification settings. A message, an
   * @mention or a reply then drew nothing, anywhere. A row in this store is a
   * banner somebody is owed.
   */
  if (!hasRows || callInProgress) return null;

  return (
    <MessagePopupCard
      rows={rows}
      onOpen={onOpen}
      onDismiss={popupActions.dismiss}
      onViewAll={onViewAll}
      onPauseChange={popupActions.setPaused}
    />
  );
}
